/* eslint-disable no-await-in-loop, no-continue */
import { z } from 'zod';
import { CONFIG } from '@frameio/config';
import { sleep } from '../async/sleep';
import { removeBearerPrefix } from './tokenPrefix';

const refreshTokenResponseSchema = z.object({
  errors: z
    .array(
      z.object({
        message: z.string(),
        extensions: z.object({ code: z.string().optional() }).optional(),
      })
    )
    .optional(),
  data: z
    .object({
      cycleRefreshToken: z
        .object({
          token: z.object({
            accessToken: z.string(),
            refreshToken: z.string().uuid().nullish(),
            expiration: z.number(),
            sessionToken: z.string().nullable(),
          }),
        })
        .nullable(),
    })
    .nullable()
    .optional(),
});

const uuidSchema = z.string().uuid();
const stringSchema = z.string();

const RETRY_DELAYS = [10000, 30000, 60000]; // 10s, 30s, 1m in milliseconds

export class TransientError extends Error {}
export class CredentialRejectionError extends Error {}
export class InvalidRefreshRequestError extends Error {}

export const fetchNewAccessToken = async ({
  accessToken,
  headers = {},
  clientName = CONFIG.thirdParty.apolloClient?.name,
  clientVersion,
  refreshToken,
  sessionToken,
  signal,
}: {
  accessToken?: string;
  headers?: Record<string, string>;
  clientName?: string;
  clientVersion: string;
  refreshToken?: string;
  sessionToken?: string;
  signal?: AbortSignal;
}) => {
  // throws if refresh token or session token are not valid uuids
  try {
    if (accessToken) {
      stringSchema.parse(accessToken);
    }
    if (refreshToken) {
      uuidSchema.parse(refreshToken);
    }
    if (sessionToken) {
      stringSchema.parse(sessionToken);
    }
  } catch (error) {
    throw new InvalidRefreshRequestError('Invalid refresh request', {
      cause: error,
    });
  }

  let graphQLResponse: z.infer<typeof refreshTokenResponseSchema> | null = null;
  for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt += 1) {
    signal?.throwIfAborted();
    // eslint-disable-next-line no-await-in-loop
    const fetchResponse = await fetch(CONFIG.graphqlUrl, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        'apollographql-client-name': clientName!,
        'apollographql-client-version': clientVersion!,
        'x-gql-op': 'RefreshAccessToken',
        ...headers,
      } as const,
      body: JSON.stringify({
        operationName: 'RefreshAccessToken',
        query: `mutation RefreshAccessToken($input: CycleRefreshTokenInput!) {
            cycleRefreshToken(input: $input) {
              token {
                accessToken
                refreshToken
                expiration
                sessionToken
              }
            }
          }`,
        variables: {
          input: {
            accessToken: removeBearerPrefix(accessToken),
            refreshToken,
            sessionToken,
          },
        },
      }),
    }).catch((error: unknown) => {
      signal?.throwIfAborted();
      throw new TransientError('Unable to reach token refresh service', {
        cause: error,
      });
    });

    /**
     * As of Jan 2025, rate limit is 10 requests per second.
     * Normal users should never encounter this rate limit, but it is often
     * encountered in tests.
     */
    if (!fetchResponse.ok && fetchResponse.status === 429) {
      if (attempt === RETRY_DELAYS.length) {
        throw new TransientError(
          'Rate limit exceeded while refreshing access token'
        );
      }
      // eslint-disable-next-line no-console
      console.log(
        `Rate limit exceeded (attempt ${attempt + 1}), retrying in ${
          RETRY_DELAYS[attempt] / 1000
        } seconds...`
      );
      await sleep(RETRY_DELAYS[attempt]);
      continue;
    }

    // Network error
    if (!fetchResponse.ok) {
      throw new TransientError(`Failed to refresh access token`);
    }

    try {
      graphQLResponse = refreshTokenResponseSchema.parse(
        await fetchResponse.json()
      );
    } catch (error) {
      throw new TransientError('Invalid token refresh response', {
        cause: error,
      });
    }
    if (graphQLResponse.errors?.length) {
      const rejected = graphQLResponse.errors.every(({ extensions }) =>
        ['BAD_REQUEST', 'UNAUTHENTICATED', 'PERMISSION_DENIED'].includes(
          extensions?.code ?? ''
        )
      );
      if (rejected) {
        throw new CredentialRejectionError('Refresh credentials rejected');
      }
      throw new TransientError('Token refresh service returned an error');
    }
    const token = graphQLResponse.data?.cycleRefreshToken?.token;
    if (!token) {
      throw new TransientError('Missing credentials in token refresh response');
    }
    return token;
  }
  throw new Error('Unknown error while refreshing access token');
};
